import { eventDateTimeToUtcMilli } from "../eventDateTime";
import EventReportSheet from "./EventReportSheet";
import EventWaitlistModal from "./EventWaitlistModal";
import EventAttendeeCheckInModal from "./EventAttendeeCheckInModal";
import EventScheduleDialog from "../EventScheduleDialog";
import { useManageEventSchedule } from "./useManageEventSchedule";
import React, { useMemo, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { eventDesignFont, eventDesignTheme } from "../eventDesignTheme";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Paper,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { RoutePaths } from "../../../../enumStrings";
import {
  CmpEventStatusEnum,
  CmpEventVisibilityEnum,
  GetCmpManageEventsQuery,
  useCancelCmpEventMutation,
  useCompleteCmpEventMutation,
  useDeleteCmpEventMutation,
  useDeleteCmpEventDraftMutation,
  useDuplicateCmpEventAsDraftMutation,
  useGetCmpEventsAnalyticsQuery,
  useGetCmpEventTypesQuery,
  useGetCmpEventDetailLazyQuery,
  useGetCmpManageEventsQuery,
  usePublishCmpEventMutation,
  useUnpublishCmpEventMutation,
} from "../../../../graphql/types";
import { eventReturnToFormState } from "../eventBackendMappers";
import { useCmpGlobalSettings } from "../../../../hooks/useCmpSettingsSection";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import { ActionSuccessDialog } from "../../../common/dashboard/ActionSuccessDialog";
import { ConfirmDialog } from "../../../common/dashboard/ConfirmDialog";
import { TableEmptyState } from "../../../common/dashboard/TableEmptyState";
import { normalizeCmpTimeZone } from "../../../../utils/cmpTimeZones";
import { toEventTypeOptions } from "../eventBackendMappers";
import {
  cpaGold,
  cpaGoldDark,
  formatCurrencyFromCents,
} from "../EventSales/eventSalesShared";
import { eventStatusActionApolloContext } from "../EventSales/eventSalesApolloErrorPolicy";
import {
  DataTable,
  DataTableColumn,
} from "../../../common/dashboard/DataTable";
import {
  ClearFiltersButton,
  FilterBar,
  FilterMenuButton,
  FilterSearchField,
  clearFiltersButtonSx,
  filterFieldSx,
} from "../../../common/dashboard/FilterBar";
import { ManagePageShell } from "../../../common/dashboard/ManagePageShell";
import { AnchoredMenu } from "../../../common/dashboard/AnchoredMenu";
import {
  buildRowActions,
  RowActionMenu,
  RowActionsTrigger,
} from "../../../common/dashboard/RowActionMenu";
import { StatusBadge } from "../../../common/dashboard/StatusBadge";
import { DateRangeFilterPanel } from "../../../common/dashboard/DateRangeFilterField";
import EventDashboardPixelQa, {
  getDashboardPixelQaState,
} from "../pixelQa/EventDashboardPixelQa";
import EventSalesSearchableMultiSelectMenu from "../EventSales/EventSalesSearchableMultiSelectMenu";
import { sortManageEventsForDefaultView } from "./manageEventsOrdering";
import { groupManageEventDrafts } from "./manageEventsDrafts";
import { ConfirmableAction, confirmSpecs, isConfirmableAction } from "./manageEventsConfirmSpecs";
import { buildStaffErrorFallback, getUserFacingErrorText, UserFacingError } from "../../../../utils/userFacingError";

type ManageEventRow =
  GetCmpManageEventsQuery["getCmpManageEvents"]["events"][number] & {
    // A side draft saved before Oct 2026 (CMP_EVENT_DRAFT) with no event row
    // of its own. It shows as Draft; Edit opens it and Save Draft turns it
    // into a real Draft event (Monday 13290832990).
    draftId?: string; draftRevision?: number; sourceEventId?: string | null;
    // A saved event row with an older side draft opens that draft on Edit.
    workingDraftId?: string;
  };

type DerivedStatus =
  | "live"
  | "private"
  | "canceled"
  | "closed"
  | "draft"
  | "scheduled"
;

type ActionKey =
  | "edit"
  | "duplicate"
  | "schedule"
  | "copyGuestLink"
  | "waitlist"
  | "exportSponsors"
  | "checkInAttendees"
  | "cancel"
  | "close"
  | "setDraft"
  | "unpublish"
  | "publish"
  | "unschedule"
  | "attendeeList"
  | "delete";

type SortColumn =
  | "name"
  | "date"
  | "ticketsSold"
  | "ticketRevenue"
  | "sponsorsSold"
  | "sponsorshipRevenue"
  | "totalRevenue"
  | "status";

type SortState = { column: SortColumn; direction: "asc" | "desc" };

// Claude Design (4) Manage Events: plain dark status text, red for Closed.
const statusMeta: Record<DerivedStatus, { label: string; color: string }> = {
  live: { label: "Published", color: "#0A0A0A" },
  private: { label: "Published", color: "#0A0A0A" },
  // Design (15) ME:504 Canadian spelling; the stored status stays CANCELED.
  canceled: { label: "Cancelled", color: "#0A0A0A" },
  closed: { label: "Closed", color: "#F0202E" },
  draft: { label: "Draft", color: "#0A0A0A" },
  scheduled: { label: "Scheduled", color: "#0A0A0A" },
};

const ConfirmGlyph = ({ paths }: { paths: string[] }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 29, height: 29 }} aria-hidden="true">
    {paths.map((d) => <path key={d} d={d} />)}
  </svg>
);

// Design (19) ME:643 statuses: Published, Scheduled, Draft, Cancelled, Closed.
// A leftover side draft row is a Draft.
const deriveStatus = (event: ManageEventRow): DerivedStatus => {
  if (event.draftId) return "draft";
  if (event.status === CmpEventStatusEnum.Scheduled) return "scheduled";
  if (event.status === CmpEventStatusEnum.Canceled) {
    return "canceled";
  }
  if (event.status === CmpEventStatusEnum.Completed) {
    return "closed";
  }
  if (event.status === CmpEventStatusEnum.Draft) {
    return "draft";
  }
  // Ended published events are read-only. Public discovery retains them for
  // 30 days; stored publication and visibility remain independent.
  if (
    typeof event.endAtUtcMilli === "number" &&
    event.endAtUtcMilli < Date.now()
  ) {
    return "closed";
  }
  return event.visibility === CmpEventVisibilityEnum.Private ? "private" : "live";
};

// Source menus, with the current unpublish, guest-link and waitlist requirements.
// Duplicate Event sits where Manage Events Screen.dc.html ~495-503 puts it:
// second in every menu and first on Closed.
const actionsByStatus: Record<DerivedStatus, ActionKey[]> = {
  // Manage Events Screen (16) menus. Waitlist (events that allow one) and
  // Copy Private Guest Link (private events) are approved differences.
  live: ["edit", "duplicate", "attendeeList", "exportSponsors", "checkInAttendees", "waitlist", "unpublish", "cancel", "close"],
  private: ["edit", "duplicate", "copyGuestLink", "attendeeList", "exportSponsors", "checkInAttendees", "waitlist", "unpublish", "cancel", "close"],
  canceled: ["edit", "duplicate", "setDraft", "delete"],
  closed: ["duplicate", "attendeeList", "exportSponsors"],
  draft: ["edit", "duplicate", "publish", "schedule", "attendeeList", "exportSponsors", "cancel", "delete"],
  scheduled: ["edit", "duplicate", "attendeeList", "exportSponsors", "checkInAttendees", "unschedule", "cancel", "close"],
};
// A leftover side draft has no saved event yet, so it has no sales, copy,
// publish or cancel of its own: Edit (Save Draft makes it a real Draft
// event), Schedule Event (opens it in the form with the schedule pop-up, the
// only place it can become an event) and Delete.
const sideDraftActions: ActionKey[] = ["edit", "schedule", "delete"];

const actionLabel: Record<ActionKey, string> = {
  copyGuestLink: "Copy Private Guest Link",
  waitlist: "Waitlist",
  edit: "Edit Event",
  duplicate: "Duplicate Event",
  schedule: "Schedule Event",
  exportSponsors: "View/Export Sponsor Report",
  attendeeList: "View/Export Attendee Report",
  publish: "Publish Event",
  unschedule: "Unscheduled (Back to Draft)",
  checkInAttendees: "View & Check-in Attendees",
  cancel: "Cancel Event",
  close: "Close Event Registration",
  setDraft: "Set Back to Draft",
  unpublish: "Unpublish Event (Back to Draft)",
  delete: "Delete Event",
};

const dangerActions = new Set<ActionKey>(["cancel", "delete"]);

// Task words for the error fallback: "We could not [task]. Try again..."
const getEventActionTask = (action: ActionKey, name: string): string => {
  switch (action) {
    case "cancel":
      return `cancel ${name}`;
    case "close":
      return `close ${name}`;
    case "publish":
      return `publish ${name}`;
    case "delete":
      return `delete ${name}`;
    case "setDraft":
    case "unpublish":
    case "unschedule":
      return `set ${name} back to Draft`;
    default:
      return `update ${name}`;
  }
};

// Shared row-action wiring (B7): RowActionMenu's defaults are the Claude
// Design Manage Events menu (label-only items, EV-40), so only the matrix rides
// along here. Module scope is SAFE for this const — it touches only page-local
// consts, never RoutePaths/enumStrings (the circular-import TDZ trap
// documented in ManagePageShell.tsx).
const getCommunityMenu = (event: ManageEventRow) => {
  const status = deriveStatus(event);
  return status === "closed" ? "communityClosed" : status === "draft" ? "communityDraft" : "communityOpen";
};

const buildEventRowActions = buildRowActions<DerivedStatus | "closedBeforeEnd" | "communityDraft" | "communityOpen" | "communityClosed" | "sideDraft", ActionKey, ManageEventRow>(
  {
    // Design (15) ME:499 Community menus (decision Q5). Delete Event on all
    // three and no Close on a Draft are Moeen's decisions (St. Andrews, Nov 3
    // 2026): Close published the Draft, and staff had no way to remove either.
    matrix: { ...actionsByStatus, closedBeforeEnd: [...actionsByStatus.closed, "checkInAttendees"], communityDraft: ["edit", "duplicate", "delete"], communityOpen: ["edit", "duplicate", "close", "delete"], communityClosed: ["duplicate", "delete"], sideDraft: sideDraftActions },
    labels: actionLabel,
    getStatus: event => event.draftId ? "sideDraft" : event.eventKind === "COMMUNITY" ? getCommunityMenu(event)
      // Close Event Registration ends ticket sales; check-in stays until the
      // event ends (Guide - Manage Events.dc.html:151, Moeen Nov 2 2026).
      : event.status === CmpEventStatusEnum.Completed && typeof event.endAtUtcMilli === "number" && event.endAtUtcMilli > Date.now() ? "closedBeforeEnd" : deriveStatus(event),
    destructiveActions: dangerActions,
    // Screenshot shows a divider isolating Delete Event at the bottom of the
    // Draft/Canceled/Closed menus.
    dividerBeforeActions: new Set<ActionKey>(["delete"]),
  }
);

// Design (19) ME:688 status filter: Published, Scheduled, Draft, Cancelled, Closed.
const statusFilterOrder: DerivedStatus[] = ["live", "scheduled", "draft", "canceled", "closed"];
const statusFilterLabel = (value: DerivedStatus) => statusMeta[value].label;

// Design (15) ds/SearchableSelect.jsx:136-139: the button shows the first
// pick and "+N" for the rest.
export const filterPickLabel = (picks: readonly string[], placeholder: string) =>
  picks.length === 0 ? placeholder : picks.length === 1 ? picks[0] : `${picks[0]} +${picks.length - 1}`;

const getRowActions = (event: ManageEventRow) => buildEventRowActions(event)
  .filter(action => (action.key !== "waitlist" || event.allowWaitlist) &&
    (action.key !== "copyGuestLink" || !!event.privateGuestUrl))
  // Design: Delete Event is the only semibold item.
  .map(action => action.key === "delete" ? { ...action, strong: true } : action);

// Schedule Event opens the pop-up over the list (Manage Events Screen.dc.html:
// 340-369). A side draft has no event to schedule yet, and a saved event with
// a newer side draft must schedule that draft, so both still open the form
// with ?schedule=true (old links to that address keep working).
const schedulesInForm = (event: ManageEventRow) =>
  Boolean(event.draftId || event.workingDraftId);

const eventEditPath = (event: ManageEventRow, schedule = false) => {
  const path = event.draftId && !event.sourceEventId ? RoutePaths.chamberAdmin_events_create : RoutePaths.chamberAdmin_events_update.replace(":eventId", event.sourceEventId || event.id);
  const query = new URLSearchParams();
  const draftId = event.draftId || event.workingDraftId;
  if (draftId) query.set("draftId", draftId);
  if (schedule) query.set("schedule", "true");
  // toString(), not query.size: size is missing in older Safari (< 17).
  const queryString = query.toString();
  return `${path}${queryString ? `?${queryString}` : ""}`;
};

// Community events have no sales, and a side draft has none of its own.
const hasNoSalesStats = (event: ManageEventRow) =>
  event.eventKind === "COMMUNITY" || !!event.draftId;

// Tickets Sold counts every place held, including a staff sponsorship's
// places before its attendees are named (IF11 Q9).
const getManageEventTicketsSold = (event: ManageEventRow) =>
  Math.max(event.attendeeCount, event.reservedAttendeeCount ?? 0);

// Dates/times must render in the CHAMBER's time zone (matching what the
// wizard captured), not the viewer's browser zone.
const makeEventDateTimeFormatters = (timeZone: string) => {
  // Design dateLabel: "Sep 5, 2026".
  const dateFormatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone,
  });
  // en-US on purpose: the screenshot time range reads "6:00 PM – 10:00 PM"
  // (en-CA renders "6:00 p.m.").
  const timeFormatter = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone,
  });
  return {
    formatDate: (utcMilli: number) => Number.isFinite(utcMilli) ? dateFormatter.format(new Date(utcMilli)) : "Date TBD",
    // En dash separator per the manage screenshot.
    formatTimeRange: (startUtcMilli: number, endUtcMilli: number) =>
      !Number.isFinite(startUtcMilli) || !Number.isFinite(endUtcMilli) ? "" : `${timeFormatter.format(new Date(startUtcMilli))} – ${timeFormatter.format(
        new Date(endUtcMilli)
      )}`,
  };
};

// Design tickets-sold tiers (ME renderVals): 70%+ green, 40%+ gold, above 0
// red; an empty bar with a grey 0% label.
const ticketsSoldTierColor = (pct: number): string => {
  if (pct >= 70) {
    return "#10B981";
  }
  if (pct >= 40) {
    return cpaGold;
  }
  if (pct > 0) {
    return "#F0202E";
  }
  return "#9E9E9E";
};

// Filter-row control styling now comes from the shared FilterBar module
// (filterFieldSx / filterButtonSx / activeFilterButtonSx /
// clearFiltersButtonSx are byte-identical to the deleted local copies except
// the shared width:100% + idle color #6B7280, both undone per-instance via
// the FilterMenuButton/ClearFiltersButton sx escape hatches below).

// Mirrors the Manage Invoices date-range menu so CalendarDateInput popovers
// are not clipped by the menu surface.
// Claude Design ds-components/DateRangeFilter.jsx popover surface (same as
// the Manage Invoices Invoice Date Range).
const dateRangeMenuPaperSx = {
  border: "1px solid #ECECEC",
  borderRadius: "12px",
  boxShadow: "0 12px 32px rgba(0,0,0,.14)",
  boxSizing: "border-box",
  maxHeight: "92vh",
  maxWidth: "calc(100vw - 24px)",
  mt: "8px",
  overflow: "visible",
  p: "22px",
  width: 420,
} as const;

// Row "Actions ⌄" trigger sx, extracted verbatim from the old inline Button
// sx for the shared RowActionsTrigger. The old local actionMenuPaperSx /
// paginationSx / pageSizeSelectSx duplicates are gone — RowActionMenu's
// rowActionMenuPaperSx and DataTable's dataTablePaginationSx /
// dataTablePageSizeSelectSx defaults are byte-identical ManageEvents copies.
const eventsActionButtonSx = {
  backgroundColor: "white",
  borderColor: "#C7C7C7",
  borderRadius: "8px",
  color: "#0A0A0A",
  fontSize: 13,
  fontWeight: 600,
  height: 36,
  px: 1.75,
  textTransform: "none",
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: "#F7F7F8",
    borderColor: "#C7C7C7",
  },
} as const;

const AnalyticsCard = ({
  label,
  value,
  current,
  prior,
}: {
  label: string;
  value: string;
  current: number;
  prior: number;
}) => {
  const change =
    prior === 0 ? (current > 0 ? 100 : 0) : ((current - prior) / prior) * 100;
  const pct = Math.abs(Math.round(change));
  // Verbatim delta casing from the manage screenshot (spacing quirks
  // normalized): positive "X% Increase from last year" in green, negative
  // "X% decrease from last year" in red, zero stays grey with "Increase".
  const deltaText =
    change < 0
      ? `${pct}% decrease from last year`
      : `${pct}% Increase from last year`;
  // Design stat cards: green unless the value decreased.
  const deltaColor = change < 0 ? "#F0202E" : "#10B981";
  return (
    <Paper
      elevation={0}
      sx={{ borderRadius: "12px", boxShadow: "0 1px 2px rgba(0,0,0,.04)" }}
      className="flex flex-col gap-[10px] border border-[#E0E0E0] px-[18px] py-[16px]"
    >
      <p className="text-[13px] font-normal text-[#0A0A0A]">{label}</p>
      <p className="whitespace-nowrap text-[18px] font-bold text-[#0A0A0A]">{value}</p>
      <p className="text-[12px]" style={{ color: deltaColor }}>
        {deltaText}
      </p>
    </Paper>
  );
};

const ManageEventsContent = (): React.ReactElement => {
  const navigate = useNavigate();
  const noun = useCmpOrganizationNoun();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";

  const { data: settingsData } = useCmpGlobalSettings(chamberId);
  const chamberTimeZone = normalizeCmpTimeZone(
    settingsData?.getChamberMemberProSettings.setup.timeZone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      "America/Toronto"
  ).timeZone;
  const [sponsorListEventId, setSponsorListEventId] = useState<string | null>(null);
  const eventSchedule = useManageEventSchedule({ chamberId, chamberTimeZone });
  const [checkInEventId, setCheckInEventId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState<string[]>([]);
  const [eventTypeFilter, setEventTypeFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<DerivedStatus[]>([]);
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [sort, setSort] = useState<SortState | null>(null);
  const [feedback, setFeedback] = useState<{
    tone: "success" | "error";
    message: string;
    highlights?: readonly string[];
    note?: string;
  } | null>(null);
  const [locationMenuAnchor, setLocationMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [typeMenuAnchor, setTypeMenuAnchor] = useState<HTMLElement | null>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [dateMenuAnchor, setDateMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    event: ManageEventRow;
    action: ActionKey;
  } | null>(null);
  const [actionMenu, setActionMenu] = useState<{
    event: ManageEventRow;
    el: HTMLElement;
  } | null>(null);

  // ----- Page header ("Manage Events" band with the gold add button) --------
  const pageHeaderActions = useMemo(
    () => (
      // ME:53-54 (EV15-67): 14px/600, "+" glyph, 12px 22px padding.
      <Button
        variant="contained"
        onClick={() => navigate(RoutePaths.chamberAdmin_events_create)}
        sx={{
          alignSelf: { xs: "flex-start", sm: "center" },
          backgroundColor: cpaGold,
          borderRadius: "8px",
          boxShadow: "none",
          fontSize: 14,
          fontWeight: 600,
          gap: "8px",
          px: "22px",
          py: "12px",
          textTransform: "none",
          whiteSpace: "nowrap",
          "&:hover": { backgroundColor: cpaGoldDark, boxShadow: "none" },
        }}
      >
        <span aria-hidden="true" style={{ fontSize: 17, lineHeight: 1 }}>+</span> Add New Event
      </Button>
    ),
    [navigate]
  );

  // Handed to ManagePageShell, which owns the set/cleanup effect. Built
  // INSIDE the component with useMemo (NOT at module scope): the actions
  // button reaches RoutePaths via navigate, and enumStrings imports this page
  // for the route registry — module-init evaluation hits the circular-import
  // TDZ and white-screens the app on cold load (see ManagePageShell.tsx).
  // Title is deliberately NOT set — it stays route-sourced ("Manage Events").
  const headerOverride = useMemo(
    () => ({
      description: `View, manage and track all of your ${noun.singular} events.`,
      actions: pageHeaderActions,
      withHeaderDivider: true,
      horizontalPaddingClassName: "px-[16px] md:px-[40px]",
      headerSpacingClassName: "pt-[32px] pb-[16px] md:pt-[calc(32px-1rem)]",
      titleClassName: "text-[28px] font-bold leading-tight text-[#0A0A0A]",
      titleStyle: { fontFamily: eventDesignFont },
      descriptionClassName: "mt-[6px] text-sm text-[#0A0A0A]",
      descriptionStyle: { fontFamily: eventDesignFont, fontSize: 14 },
    }),
    [pageHeaderActions, noun]
  );

  // ----- Queries -------------------------------------------------------------
  const {
    data: eventsData,
    loading: eventsLoading,
    refetch: refetchEvents,
  } = useGetCmpManageEventsQuery({
    variables: { input: { chamberId } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const events = useMemo<ManageEventRow[]>(() => {
    const { savedRows: saved, draftRows: drafts } = groupManageEventDrafts({
      drafts: eventsData?.getCmpManageEvents.drafts || [],
      events: eventsData?.getCmpManageEvents.events || [],
    });
    return [...saved, ...drafts.map(draft => {
      const original = saved.find(event => event.id === draft.eventId);
      // The row shows the draft's own date, type and location (backend
      // getCmpEventDraftSummaries reads them from the stored draft).
      return { ...original, id: `draft:${draft.id}`, draftId: draft.id, draftRevision: draft.revision, sourceEventId: draft.eventId,
        title: draft.title || "Untitled draft", slug: original?.slug || "", eventCode: original?.eventCode || null,
        eventTypeLabel: draft.eventTypeLabel || original?.eventTypeLabel || "",
        eventTypeColor: draft.eventTypeColor || original?.eventTypeColor || "#6B7280",
        status: CmpEventStatusEnum.Draft, publishAtUtcMilli: null,
        startAtUtcMilli: draft.startAtUtcMilli ?? original?.startAtUtcMilli ?? NaN,
        endAtUtcMilli: draft.endAtUtcMilli ?? original?.endAtUtcMilli ?? NaN,
        // Sales belong to the saved event row, not to its side draft.
        attendeeCount: 0, reservedAttendeeCount: 0, maxAttendeeCapacity: original?.maxAttendeeCapacity ?? null,
        ticketRevenueInCents: 0, sponsorshipRevenueInCents: 0,
        sponsorsSold: 0, visibility: original?.visibility || CmpEventVisibilityEnum.Private,
        locationLabel: draft.locationLabel || original?.locationLabel || "", paymentActionRequiredReason: null,
        allowWaitlist: original?.allowWaitlist ?? false };
    })];
  }, [eventsData]);

  const { data: analyticsData, refetch: refetchAnalytics } =
    useGetCmpEventsAnalyticsQuery({
      variables: { input: { chamberId } },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
    });
  const analytics = analyticsData?.getCmpEventsAnalytics;

  const { data: eventTypesData } = useGetCmpEventTypesQuery({
    variables: { input: { chamberId, includeArchived: false } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const eventTypeOptions = useMemo(
    () => toEventTypeOptions(eventTypesData?.getCmpEventTypes.eventTypes || []),
    [eventTypesData]
  );
  const locationOptions = useMemo(
    () =>
      Array.from(new Set(events.map((event) => event.locationLabel))).filter(
        Boolean
      ),
    [events]
  );

  // ----- Mutations + exports -------------------------------------------------
  const [unpublishEvent] = useUnpublishCmpEventMutation();
  const [publishEvent] = usePublishCmpEventMutation();
  // Publish Event loads the saved event first; a failed load shows in the
  // page message instead of /500 (audit 3.1 item 4).
  const [loadEventDetail] = useGetCmpEventDetailLazyQuery({
    fetchPolicy: "network-only",
    context: eventStatusActionApolloContext,
  });
  const [attendeeListEventId, setAttendeeListEventId] = useState<string | null>(null);
  // IF11 EV12: the event reports open the Reports sheet for that event.
  const attendeeListEvent = events.find((event) => event.id === attendeeListEventId) || null;
  const sponsorListEvent = events.find((event) => event.id === sponsorListEventId) || null;
  const [cancelEvent] = useCancelCmpEventMutation();
  const [completeEvent] = useCompleteCmpEventMutation();
  const [waitlistEventId, setWaitlistEventId] = useState<string | null>(null);
  const [deleteDraft] = useDeleteCmpEventDraftMutation();
  const [deleteEvent] = useDeleteCmpEventMutation();
  const [duplicateEvent] = useDuplicateCmpEventAsDraftMutation({ context: eventStatusActionApolloContext });
  const [actionLoading, setActionLoading] = useState(false);

  // ----- Filtering + sorting ---------------------------------------------------
  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      const timeZone = event.timeZone || chamberTimeZone;
      const fromUtcMilli = dateFromFilter ? eventDateTimeToUtcMilli(dateFromFilter, "00:00", timeZone) : null;
      const toUtcMilli = dateToFilter ? eventDateTimeToUtcMilli(dateToFilter, "23:59", timeZone) + 59999 : null;
      if (searchQuery.trim() && !event.title.toLowerCase().includes(searchQuery.trim().toLowerCase())) {
        return false;
      }
      if (locationFilter.length > 0 && !locationFilter.includes(event.locationLabel)) {
        return false;
      }
      if (
        eventTypeFilter.length > 0 &&
        !eventTypeFilter.includes(event.eventTypeLabel)
      ) {
        return false;
      }
      if (statusFilter.length > 0 && !statusFilter.includes(deriveStatus(event) === "private" ? "live" : deriveStatus(event))) {
        return false;
      }
      if (fromUtcMilli !== null && event.startAtUtcMilli < fromUtcMilli) {
        return false;
      }
      if (toUtcMilli !== null && event.startAtUtcMilli > toUtcMilli) {
        return false;
      }
      return true;
    });
  }, [
    events,
    searchQuery,
    chamberTimeZone,
    locationFilter,
    eventTypeFilter,
    statusFilter,
    dateFromFilter,
    dateToFilter,
  ]);

  const sortedEvents = useMemo(() => {
    if (!sort) {
      return sortManageEventsForDefaultView({
        events: filteredEvents,
        nowUtcMilli: Date.now(),
      });
    }
    const sortValue = (event: ManageEventRow): string | number => {
      switch (sort.column) {
        case "name":
          return event.title.toLowerCase();
        case "date":
          return event.startAtUtcMilli;
        case "ticketsSold":
          // ME:518: Tickets Sold sorts by percent sold (EV15-67).
          // Without a limit, sort by the count after the limited events.
          return event.maxAttendeeCapacity
            ? getManageEventTicketsSold(event) / event.maxAttendeeCapacity
            : getManageEventTicketsSold(event) / Number.MAX_SAFE_INTEGER;
        case "ticketRevenue":
          return event.ticketRevenueInCents;
        case "sponsorsSold":
          return event.sponsorsSold;
        case "sponsorshipRevenue":
          return event.sponsorshipRevenueInCents;
        case "totalRevenue":
          return event.ticketRevenueInCents + event.sponsorshipRevenueInCents;
        case "status":
        default:
          return statusMeta[deriveStatus(event)].label;
      }
    };
    const factor = sort.direction === "asc" ? 1 : -1;
    return [...filteredEvents].sort((a, b) => {
      const valueA = sortValue(a);
      const valueB = sortValue(b);
      if (valueA < valueB) {
        return -1 * factor;
      }
      if (valueA > valueB) {
        return 1 * factor;
      }
      return 0;
    });
  }, [filteredEvents, sort]);

  const pagedEvents = sortedEvents;

  const locationFilterIsActive = locationFilter.length > 0;
  const typeFilterIsActive = eventTypeFilter.length > 0;
  const statusFilterIsActive = statusFilter.length > 0;
  const dateFilterIsActive =
    dateFromFilter.length > 0 || dateToFilter.length > 0;

  const clearFilters = () => {
    setSearchQuery("");
    setLocationFilter([]);
    setEventTypeFilter([]);
    setStatusFilter([]);
    setDateFromFilter("");
    setDateToFilter("");
  };

  const toggleSort = (column: SortColumn) =>
    setSort((current) =>
      current?.column === column
        ? { column, direction: current.direction === "asc" ? "desc" : "asc" }
        : { column, direction: "asc" }
    );

  const refetchAll = async () => {
    await Promise.all([refetchEvents(), refetchAnalytics()]);
  };

  // ----- Status actions ------------------------------------------------------
  const runAction = async (event: ManageEventRow, action: ActionKey) => {
    const input = { chamberId, eventId: event.id, expectedUpdatedAtUtcMilli: event.updatedAtUtcMilli };
    setActionLoading(true);
    try {
      switch (action) {
        case "cancel":
          await cancelEvent({ variables: { input }, context: eventStatusActionApolloContext });
          break;
        case "close":
          await completeEvent({ variables: { input }, context: eventStatusActionApolloContext });
          break;
        case "setDraft":
        case "unpublish":
        case "unschedule":
          await unpublishEvent({ variables: { input }, context: eventStatusActionApolloContext });
          break;
        case "publish": {
          // Manage Events Screen (16) Publish Event: the same Step 1-4 checks
          // as Publish in the wizard. A Draft that is not ready is never
          // published; the message names the step to fix.
          const detail = await loadEventDetail({ variables: { input: { chamberId, eventId: event.id } } });
          const savedEvent = detail.data?.getCmpEventDetail.event;
          if (!savedEvent) throw new UserFacingError("The event could not be loaded. Refresh the page and try again.");
          const eventTimeZone = normalizeCmpTimeZone(savedEvent.timeZone || chamberTimeZone).timeZone;
          // Loaded on demand: the wizard module carries the rich text editor.
          const { getEventValidationIssuesByStep } = await import("../CreateEvent/wizard/EventWizard");
          const issues = getEventValidationIssuesByStep(eventReturnToFormState(savedEvent, eventTimeZone), eventTimeZone, true);
          if (issues.length) {
            const name = event.title || "Untitled Event";
            throw new UserFacingError(`${name} is not ready to publish. Go to Actions > Edit Event and fix these items: ${issues
              .map((group) => `Step ${group.step} ${group.title}: ${group.messages.join(" ")}`).join(" ")}`);
          }
          await publishEvent({ variables: { input }, context: eventStatusActionApolloContext });
          break;
        }
        case "delete":
          if (event.draftId) await deleteDraft({ variables: { input: { chamberId, draftId: event.draftId, expectedRevision: event.draftRevision! } }, context: eventStatusActionApolloContext });
          else await deleteEvent({ variables: { input }, context: eventStatusActionApolloContext });
          break;
        default:
          break;
      }
      await refetchAll();
      const name = event.title || "Untitled Event";
      setFeedback(isConfirmableAction(action)
        ? { tone: "success", message: confirmSpecs[action].done(name), highlights: [name] }
        : { tone: "success", message: `${name} has been updated.`, highlights: [name] });
    } catch (error) {
      setFeedback({
        tone: "error",
        message: getUserFacingErrorText(
          error,
          buildStaffErrorFallback(getEventActionTask(action, event.title || "Untitled Event"))
        ),
      });
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  // EV-N1: the backend copies the saved event (never a side draft) as a
  // Draft named "<name> (Copy)" or "(Copy N)" with sales reset.
  const runDuplicate = async (event: ManageEventRow) => {
    setActionLoading(true);
    try {
      const result = await duplicateEvent({ variables: { input: { chamberId, eventId: event.sourceEventId || event.id } } });
      const copy = result.data?.duplicateCmpEventAsDraft;
      if (!copy?.eventSavedSuccessfully || !copy.event) throw new Error("The event could not be duplicated.");
      await refetchAll();
      const name = copy.event.title;
      setFeedback({
        tone: "success",
        message: `${name} has been created as a Draft.`,
        highlights: [name],
        note: "Edit the copy to update its date, tickets and sponsorships before scheduling or publishing it.",
      });
    } catch (error) {
      setFeedback({
        tone: "error",
        message: getUserFacingErrorText(
          error,
          buildStaffErrorFallback(`duplicate ${event.title || "Untitled Event"}`)
        ),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const onActionSelected = (event: ManageEventRow, action: ActionKey) => {
    setActionMenu(null);
    if (action === "duplicate") {
      runDuplicate(event);
      return;
    }
    if (action === "schedule" && !schedulesInForm(event)) {
      eventSchedule.open({ id: event.id, title: event.title, timeZone: event.timeZone });
      return;
    }
    if (action === "edit" || action === "schedule") {
      navigate(eventEditPath(event, action === "schedule"));
      return;
    }
    if (action === "copyGuestLink" && event.privateGuestUrl) {
      const name = event.title || "Untitled Event";
      navigator.clipboard.writeText(event.privateGuestUrl).then(() => setFeedback({ tone: "success", message: `The private guest link for ${name} has been copied.`, highlights: [name] }))
        .catch(() => setFeedback({ tone: "error", message: "We could not copy the private guest link. Try again. If it keeps happening, check that your browser lets this site copy text." }));
      return;
    }
    if (action === "waitlist") { setWaitlistEventId(event.id); return; }
    if (action === "checkInAttendees") { setCheckInEventId(event.id); return; }
    if (action === "attendeeList") { setAttendeeListEventId(event.id); return; }
    if (action === "exportSponsors") {
      setSponsorListEventId(event.id);
      return;
    }
    if (!isConfirmableAction(action)) {
      runAction(event, action);
      return;
    }
    setConfirmAction({ event, action });
  };

  // DataTable columns (B7). "none" doubles as the initial no-sort sentinel
  // (it matches no sortable column, so every arrow stack renders grey exactly
  // as before) and the non-sortable Actions column key. Cell JSX is the old
  // TableCell content verbatim; the old cell classNames ride as cellSx
  // (text-gray-950 = #030712).
  const columns: ReadonlyArray<
    DataTableColumn<ManageEventRow, SortColumn | "none">
  > = [
    {
      key: "name",
      header: "Event Name",
      sortable: true,
      render: (event) => (
        <>
          <p className="text-[14px] font-bold leading-[1.35] text-[#0A0A0A]">{event.title}</p>
          <p className="mt-[3px] text-[12px] text-[#0A0A0A]">{event.eventTypeLabel}</p>
        </>
      ),
    },
    {
      key: "date",
      header: "Event Date",
      sortable: true,
      render: (event) => (
        <>
          {/* Date is bold in the manage screenshot, matching the
              bold event name weight. */}
          <p className="whitespace-nowrap text-[14px] text-[#0A0A0A]">
            {makeEventDateTimeFormatters(event.timeZone || chamberTimeZone).formatDate(event.startAtUtcMilli)}
          </p>
          <p className="mt-[3px] whitespace-nowrap text-[12px] text-[#0A0A0A]">
            {makeEventDateTimeFormatters(event.timeZone || chamberTimeZone).formatTimeRange(event.startAtUtcMilli, event.endAtUtcMilli)}
          </p>
        </>
      ),
    },
    {
      key: "ticketsSold",
      header: "Tickets Sold",
      sortable: true,
      cellSx: { minWidth: 150 },
      render: (event) => {
        if (hasNoSalesStats(event)) return <span className="text-[14px] text-[#0A0A0A]">—</span>;
        const capacity = event.maxAttendeeCapacity || 0;
        const sold = getManageEventTicketsSold(event);
        // Design (21) CLAUDE.md "Tickets sold with no ticket limit": only the
        // number; no "/capacity", bar or percentage.
        if (capacity <= 0) {
          return <p className="text-[14px] font-bold text-[#0A0A0A]">{sold}</p>;
        }
        const pct = Math.min(100, Math.round((sold / capacity) * 100));
        const tierColor = ticketsSoldTierColor(pct);
        return (
          <>
            <p className="text-[14px] font-bold text-[#0A0A0A]">
              {sold}/{capacity}
            </p>
            <div className="mb-[4px] mt-[6px] h-[6px] w-full max-w-[120px] overflow-hidden rounded-[3px] bg-[#ECECEC]">
              <div
                className="h-[6px] rounded-[3px]"
                style={{ width: `${pct}%`, backgroundColor: tierColor }}
              />
            </div>
            <p
              className="text-[12px] font-semibold"
              style={{ color: tierColor }}
            >
              {pct}%
            </p>
          </>
        );
      },
    },
    {
      key: "ticketRevenue",
      header: "Ticket Revenue",
      sortable: true,
      cellSx: { whiteSpace: "nowrap", color: "#0A0A0A", fontSize: 14 },
      render: (event) => hasNoSalesStats(event) ? "—" : formatCurrencyFromCents(event.ticketRevenueInCents),
    },
    {
      key: "sponsorsSold",
      header: "Sponsors Sold",
      sortable: true,
      cellSx: { color: "#0A0A0A", fontSize: 14 },
      render: (event) => hasNoSalesStats(event) ? "—" : event.sponsorsSold,
    },
    {
      key: "sponsorshipRevenue",
      header: "Sponsorship Revenue",
      sortable: true,
      cellSx: { whiteSpace: "nowrap", color: "#0A0A0A", fontSize: 14 },
      render: (event) => hasNoSalesStats(event) ? "—" :
        formatCurrencyFromCents(event.sponsorshipRevenueInCents),
    },
    {
      key: "totalRevenue",
      header: "Total Revenue",
      sortable: true,
      cellSx: { whiteSpace: "nowrap", color: "#0A0A0A", fontSize: 14 },
      render: (event) => hasNoSalesStats(event) ? "—" :
        formatCurrencyFromCents(
          event.ticketRevenueInCents + event.sponsorshipRevenueInCents
        ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (event) => {
        const status = deriveStatus(event);
        // StatusBadge text variant with no secondaryLabel renders the exact
        // former <span className style={{color}}> — byte-identical DOM.
        return (
          <StatusBadge
            variant="text"
            label={statusMeta[status].label}
            textClassName="text-[14px] font-medium"
            textColor={statusMeta[status].color}
          />
        );
      },
    },
    {
      key: "none",
      header: "Actions",
      width: "140px",
      headerCellSx: { fontWeight: 700, whiteSpace: "nowrap", textAlign: "center", fontSize: 13 },
      cellSx: { textAlign: "center" },
      stickyRight: true,
      render: (event) => getRowActions(event).length ? (
        <RowActionsTrigger
          onOpen={(el) => setActionMenu({ event, el })}
          sx={eventsActionButtonSx}
        />
      ) : null,
    },
  ];

  return (
    <ManagePageShell
      className="mx-auto w-full pb-10"
      headerOverride={headerOverride}
    >
      {/* Total Event Analytics */}
      <div className="mb-4 mt-2">
        <h2 className="text-[20px] font-bold text-[#0A0A0A]">
          Total Event Analytics (Fiscal Year to Date)
        </h2>
        <p className="mt-1 text-[14px] text-[#0A0A0A]">
          See a quick overview of your {noun.singular} total year to date events
          analytics at a glance.
        </p>
      </div>
      <div className="mb-8 grid gap-[14px] [grid-template-columns:repeat(auto-fit,minmax(185px,1fr))]">
        <AnalyticsCard
          label="Total Events YTD"
          value={String(analytics?.totalEvents ?? 0)}
          current={analytics?.totalEvents ?? 0}
          prior={analytics?.priorTotalEvents ?? 0}
        />
        <AnalyticsCard
          label="Total Event Registrations YTD"
          value={String(analytics?.totalRegistrations ?? 0)}
          current={analytics?.totalRegistrations ?? 0}
          prior={analytics?.priorTotalRegistrations ?? 0}
        />
        <AnalyticsCard
          label="Total Ticket Revenue YTD"
          value={formatCurrencyFromCents(analytics?.totalTicketRevenueInCents ?? 0)}
          current={analytics?.totalTicketRevenueInCents ?? 0}
          prior={analytics?.priorTotalTicketRevenueInCents ?? 0}
        />
        <AnalyticsCard
          label="Total Sponsors Sold YTD"
          value={String(analytics?.totalSponsorsSold ?? 0)}
          current={analytics?.totalSponsorsSold ?? 0}
          prior={analytics?.priorTotalSponsorsSold ?? 0}
        />
        <AnalyticsCard
          label="Total Sponsorship Revenue YTD"
          value={formatCurrencyFromCents(
            analytics?.totalSponsorshipRevenueInCents ?? 0
          )}
          current={analytics?.totalSponsorshipRevenueInCents ?? 0}
          prior={analytics?.priorTotalSponsorshipRevenueInCents ?? 0}
        />
        <AnalyticsCard
          label="Total Event Revenue YTD"
          value={formatCurrencyFromCents(analytics?.totalEventRevenueInCents ?? 0)}
          current={analytics?.totalEventRevenueInCents ?? 0}
          prior={analytics?.priorTotalEventRevenueInCents ?? 0}
        />
      </div>

      {/* Event Filters */}
      <div className="mb-3">
        <h2 className="text-[17px] font-bold text-[#0A0A0A]">Event Filters</h2>
        <p className="mt-[4px] text-[14px] text-[#0A0A0A]">Use filters to easily sort information</p>
      </div>
      <FilterBar className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[1.65fr_1fr_1fr_1fr_1fr_auto]">
        <FilterSearchField value={searchQuery} onChange={setSearchQuery} placeholder="Search by Event Name"
          className="min-w-0" fieldSx={[filterFieldSx, { "& .MuiOutlinedInput-root": { height: 42 } }]} />
        <FilterMenuButton label={filterPickLabel(locationFilter, "Filter by Location")}
          onOpen={setLocationMenuAnchor} active={locationFilterIsActive} sx={{ height: 42, fontSize: 14 }} />
        <FilterMenuButton label={filterPickLabel(eventTypeFilter, "Filter by Event Type")}
          onOpen={setTypeMenuAnchor} active={typeFilterIsActive} sx={{ height: 42, fontSize: 14 }} />
        <FilterMenuButton label={filterPickLabel(statusFilterOrder.filter(value => statusFilter.includes(value)).map(statusFilterLabel), "Filter by Status")}
          onOpen={setStatusMenuAnchor} active={statusFilterIsActive} sx={{ height: 42, fontSize: 14 }} />
        <FilterMenuButton label="Filter by Event Date Range" onOpen={setDateMenuAnchor} active={dateFilterIsActive}
          startIcon={<CalendarTodayOutlinedIcon />} sx={{ height: 42, fontSize: 14 }} />
        <ClearFiltersButton onClick={clearFilters} sx={clearFiltersButtonSx} />
      </FilterBar>

      {/* Filter dropdowns */}
      <EventSalesSearchableMultiSelectMenu anchorEl={locationMenuAnchor} ariaLabel="Filter by Location"
        onClose={() => setLocationMenuAnchor(null)} onChange={setLocationFilter} values={locationFilter}
        searchPlaceholder="Search Location" options={locationOptions.map(value => ({ value, label: value }))} />
      <EventSalesSearchableMultiSelectMenu anchorEl={typeMenuAnchor} ariaLabel="Filter by Event Type"
        onClose={() => setTypeMenuAnchor(null)} onChange={setEventTypeFilter} values={eventTypeFilter}
        searchPlaceholder="Search Event Type" options={eventTypeOptions.map(item => ({ value: item.label, label: item.label }))} />
      <EventSalesSearchableMultiSelectMenu anchorEl={statusMenuAnchor} ariaLabel="Filter by Status"
        onClose={() => setStatusMenuAnchor(null)} onChange={setStatusFilter} values={statusFilter}
        searchPlaceholder="Search Status" options={statusFilterOrder.map(value => ({ value, label: statusFilterLabel(value) }))} />
      <AnchoredMenu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={() => setDateMenuAnchor(null)}
        variant="panel"
        PaperProps={{ "aria-label": "Event Date Range", sx: dateRangeMenuPaperSx }}
      >
        <DateRangeFilterPanel
          title="Event Date Range"
          idPrefix="event-date"
          from={dateFromFilter}
          to={dateToFilter}
          onChange={({ from, to }) => {
            setDateFromFilter(from);
            setDateToFilter(to);
          }}
          onDone={() => setDateMenuAnchor(null)}
        />
      </AnchoredMenu>

      {/* Manage Event List */}
      <div className="mb-4 flex flex-col justify-between gap-3 md:flex-row md:items-end">
        <div>
          <h2 className="text-[17px] font-bold text-[#0A0A0A]">Manage Event List</h2>
          <p className="mt-[4px] text-[14px] text-[#0A0A0A]">Preview and edit event details and status</p>
        </div>

      </div>

      {/* tableSx deliberately replaces (NOT extends) the shared dataTableSx —
          the Members default's fixed layout + tighter cell padding would
          visibly reflow this table. minWidth 1200 + MUI cell defaults are the
          page's exact former values. Pagination stays inside the table card
          per the manage screenshot; DataTable's pagination defaults
          (footer markup, "N / page" select, rounded gold pager, [10, 25, 50])
          are verbatim ManageEvents copies from Phase A. */}
      <DataTable<ManageEventRow, SortColumn | "none">
        ariaLabel="manage events table"
        columns={columns}
        rows={pagedEvents}
        getRowKey={(event) => event.id}
        sort={{
          column: (sort?.column ?? "none") as SortColumn | "none",
          direction: sort?.direction ?? "asc",
          // "none" is the non-sortable Actions column's key; renderSortLabel
          // never fires for it, so the guard is a type-narrowing no-op.
          onRequestSort: (column) => {
            if (column !== "none") {
              toggleSort(column);
            }
          },
        }}
        renderSortLabel={({ column, label, active, direction, onRequestSort }) => (
          <button
            type="button"
            onClick={() => onRequestSort(column)}
            className="inline-flex items-center gap-1 whitespace-nowrap text-[13px] font-bold text-[#0A0A0A]"
            aria-label={`Sort by ${label}`}
          >
            {label}
            <span className="flex flex-col" aria-hidden>
              <ArrowDropUpIcon
                sx={{
                  color: active && direction === "asc" ? cpaGold : "#B5B5B5",
                  fontSize: 18,
                  mb: "-11px",
                }}
              />
              <ArrowDropDownIcon
                sx={{
                  color: active && direction === "desc" ? cpaGold : "#B5B5B5",
                  fontSize: 18,
                }}
              />
            </span>
          </button>
        )}
        showLoadingRow={eventsLoading && events.length === 0}
        loadingRowContent={
          // py-4 wrapper + DataTable's default 16px cell padding reproduces
          // the old centered !py-8 (32px) message-cell geometry.
          <div className="flex justify-center py-4">
            <CircularProgress size={22} sx={{ color: cpaGold }} />
          </div>
        }
        showEmptyRow={!eventsLoading && pagedEvents.length === 0}
        emptyRowContent={
          events.length > 0 ? (
            <TableEmptyState heading="No events match your filters" onClear={clearFilters} clearLabel="Clear All Filters" />
          ) : (
            <TableEmptyState
              icon="document"
              heading="No events have been created yet"
              helper="Click Add New Event to create your first event."
            />
          )
        }
        containerClassName="w-full overflow-x-auto rounded-xl border border-gray-200 bg-white"
        tableSx={{ minWidth: 1200 }}
        rowClassName="align-top hover:bg-gray-50"
        // Claude Design (8) Manage Event List has no row-selection column
        // and no bulk action (Monday 13118964218).
      />

      {/* Single page-level Actions menu (RowActionMenu usage contract) —
          paper + destructive-red defaults are documented verbatim-ManageEvents
          copies; itemSx carries the page's former per-item sx. Edit is a real
          route link. Command actions still close first and then run through
          onActionSelected. */}
      <RowActionMenu
        actions={
          actionMenu
            ? getRowActions(actionMenu.event).map((action) =>
                (action.key === "edit" || (action.key === "schedule" && schedulesInForm(actionMenu.event)))
                  ? {
                      ...action,
                      to: eventEditPath(actionMenu.event, action.key === "schedule"),
                    }
                  : action
              )
            : []
        }
        anchorEl={actionMenu?.el ?? null}
        open={Boolean(actionMenu)}
        onClose={() => setActionMenu(null)}
        onAction={(key) => {
          if (actionMenu) {
            onActionSelected(actionMenu.event, key);
          }
        }}
      />

      {checkInEventId && chamberId && <EventAttendeeCheckInModal chamberId={chamberId} eventId={checkInEventId} onClose={() => setCheckInEventId(null)} />}
      {attendeeListEvent && chamberId && <EventReportSheet chamberId={chamberId} event={attendeeListEvent} kind="attendees" onClose={() => setAttendeeListEventId(null)} />}

      {/* Design confirm per action (ME:516-528) */}
      {(() => {
        const pending = confirmAction && isConfirmableAction(confirmAction.action) ? confirmAction : null;
        const spec = pending ? confirmSpecs[pending.action as ConfirmableAction] : null;
        const name = pending?.event.title || "this event";
        return (
          <ConfirmDialog
            open={Boolean(spec)}
            variant="confirm"
            title={spec?.title}
            message={spec ? <span className="whitespace-pre-line">{spec.bodyPre}<b>{name}</b>{spec.bodyPost}</span> : undefined}
            icon={spec ? <ConfirmGlyph paths={spec.iconPaths} /> : undefined}
            confirmLabel={spec?.confirmLabel}
            confirmColor={spec?.tone || "red"}
            loading={actionLoading}
            onConfirm={() => pending && runAction(pending.event, pending.action)}
            onClose={() => setConfirmAction(null)}
          />
        );
      })()}
      <ActionSuccessDialog
        message={feedback?.message}
        highlights={feedback?.highlights}
        note={feedback?.note}
        tone={feedback?.tone}
        onClose={() => setFeedback(null)}
      />
      <EventScheduleDialog
        open={Boolean(eventSchedule.target)}
        eventName={eventSchedule.target?.title || "Untitled Event"}
        timeZone={normalizeCmpTimeZone(eventSchedule.target?.timeZone || chamberTimeZone).timeZone}
        saving={eventSchedule.saving}
        error={eventSchedule.error}
        onCancel={eventSchedule.close}
        onConfirm={async (submission) => {
          const scheduled = await eventSchedule.confirm(submission);
          if (!scheduled) return;
          await refetchAll();
          setFeedback({ tone: "success", ...scheduled });
        }}
      />
      {sponsorListEvent && chamberId && <EventReportSheet chamberId={chamberId} event={sponsorListEvent} kind="sponsors" onClose={() => setSponsorListEventId(null)} />}
      <EventWaitlistModal chamberId={chamberId} eventId={waitlistEventId} onClose={() => setWaitlistEventId(null)} />
    </ManagePageShell>
  );
};

const ManageEvents = (): React.ReactElement => {
  const location = useLocation();
  const pixelQaState = getDashboardPixelQaState(
    location.search,
    "manage-events"
  );

  if (pixelQaState) {
    return <EventDashboardPixelQa state={pixelQaState} />;
  }

  return <ThemeProvider theme={eventDesignTheme}><div style={{ fontFamily: eventDesignFont }}><ManageEventsContent /></div></ThemeProvider>;
};

export default ManageEvents;
